import type { ComponentType } from 'react';
import { FaDiscord, FaFacebook, FaRedditAlien } from 'react-icons/fa';
import { HiOutlineMail } from 'react-icons/hi';
import type { SocialLink, SocialLinkId } from '../config/community';

const ICONS: Record<SocialLinkId, ComponentType<{ className?: string }>> = {
  facebook: FaFacebook,
  discord: FaDiscord,
  reddit: FaRedditAlien,
  email: HiOutlineMail,
};

export type SocialLinkCardProps = {
  link: SocialLink;
};

export default function SocialLinkCard({ link }: SocialLinkCardProps) {
  const Icon = ICONS[link.id];
  const external = !link.href.startsWith('mailto:');

  return (
    <a
      href={link.href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      aria-label={`${link.name}: ${link.cta}`}
      className={[
        'group relative flex min-h-44 flex-col overflow-hidden border bg-bk-panel p-5',
        'transition-[transform,border-color,background-color] duration-200',
        'hover:-translate-y-0.5 hover:bg-bk-panel-raised',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-bk-gold',
        `bg-gradient-to-br ${link.accentClass}`,
      ].join(' ')}
    >
      <div className="relative flex items-start justify-between gap-3">
        <span
          className={[
            'inline-flex h-14 w-14 shrink-0 items-center justify-center border border-bk-border bg-bk-bg/90',
            link.iconClass,
          ].join(' ')}
        >
          <Icon className="h-8 w-8" aria-hidden />
        </span>
        <span className="pt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-bk-muted group-hover:text-bk-gold">
          {link.name}
        </span>
      </div>
      <p className="relative mt-4 flex-1 text-sm leading-relaxed text-bk-muted group-hover:text-bk-text">
        {link.tagline}
      </p>
      <span
        className={[
          'relative mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider',
          link.iconClass,
        ].join(' ')}
      >
        {link.cta}
        <span
          aria-hidden
          className="transition-transform duration-200 group-hover:translate-x-1"
        >
          →
        </span>
      </span>
    </a>
  );
}
